import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { usePreferredDark } from "@vueuse/core";

type ColorMode = "light" | "dark" | "auto";

const COLOR_MODES: ColorMode[] = ["light", "dark", "auto"];
const STORAGE_KEY = "gaiup-color-mode";

const readStoredMode = (): ColorMode => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return COLOR_MODES.includes(stored as ColorMode) ? (stored as ColorMode) : "light";
};

export const useDarkModeStore = defineStore("darkMode", () => {
  const systemPrefersDark = usePreferredDark();
  // Defaults to light rather than the OS preference, so shipping this doesn't flip anyone already using the app.
  const mode = ref<ColorMode>(readStoredMode());

  const isDark = computed(() => {
    if (mode.value === "auto") return systemPrefersDark.value;
    return mode.value === "dark";
  });

  function applyClass(dark: boolean) {
    const root = document.documentElement;
    // Every element with transition-colors/duration-* would otherwise fade to the new theme on its
    // own schedule; freeze transitions for one frame so the whole page flips at once.
    root.classList.add("theme-switching");
    root.classList.toggle("dark", dark);
    root.offsetHeight;
    requestAnimationFrame(() => root.classList.remove("theme-switching"));
  }

  function setMode(newMode: ColorMode) {
    mode.value = newMode;
    localStorage.setItem(STORAGE_KEY, newMode);
  }

  function toggle() {
    setMode(isDark.value ? "light" : "dark");
  }

  watch(isDark, (dark) => applyClass(dark), { immediate: true });

  return {
    // state
    mode,

    // computed
    isDark,

    // actions
    setMode,
    toggle,
  };
});
